// Frontend client for the dashboard backend (/api/v1/dashboard/*).
// Dashboard sections read through these hooks and decide their own loading,
// empty, and unavailable states.

import {
  type GrowthHorizonKey,
  type InboundMeasureKey,
  type SourceMeasureKey,
  entryCategoryOf,
  grantsConditionalRoute,
} from "@ow/shared";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authHeaders, handleUnauthorized } from "./auth";
import { tenantHeaders } from "./tenant";

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { ...authHeaders(), ...tenantHeaders() } });
  // Dashboard routes require a login token; a 401 means it's missing or invalid
  // (e.g. AUTH_SECRET rotated) — drop the session and bounce to /login.
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error(`${path} → 401`);
  }
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return (await res.json()) as T;
}

// Admin reads and writes carry the tenant too: an upload or a "refresh now" run
// from the demo deployment must land on the demo's data, not the client's.
async function adminGet<T>(path: string, accessCode: string): Promise<T> {
  const res = await fetch(path, { headers: { "x-admin-access-code": accessCode, ...tenantHeaders() } });
  if (!res.ok) throw await adminError(path, res);
  return (await res.json()) as T;
}

async function adminPost<T>(path: string, accessCode: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json", "x-admin-access-code": accessCode, ...tenantHeaders() },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) throw await adminError(path, res);
  return (await res.json()) as T;
}

export class DashboardAdminError extends Error {
  readonly code?: string;
  readonly activeRunId?: string;

  constructor(
    readonly path: string,
    readonly status: number,
    payload: unknown,
  ) {
    const error = asAdminError(payload);
    super(error?.message ?? `${path} → ${status}`);
    this.name = "DashboardAdminError";
    this.code = error?.code;
    this.activeRunId = error?.activeRunId;
  }
}

async function adminError(path: string, res: Response) {
  const payload = await res.json().catch(() => null);
  return new DashboardAdminError(path, res.status, payload);
}

function asAdminError(payload: unknown) {
  if (!payload || typeof payload !== "object" || !("error" in payload)) return null;
  const error = (payload as { error?: unknown }).error;
  if (!error || typeof error !== "object") return null;
  const e = error as { code?: unknown; message?: unknown; activeRunId?: unknown };
  return {
    code: typeof e.code === "string" ? e.code : undefined,
    message: typeof e.message === "string" ? e.message : undefined,
    activeRunId: typeof e.activeRunId === "string" ? e.activeRunId : undefined,
  };
}

export interface GlobalTSPoint {
  year: number;
  arrivals: number;
  spend: number;
  spv: number;
}

export interface HeroTile {
  value: number;
  yoy: number;
  vs2019: number;
  year: number | null;
}

/** The set of reporting countries a length-of-stay change was computed over. */
export interface AlosBasis {
  year: number;
  countries: number;
}

/** Length of stay is the one tile whose changes can be null: the server holds
 *  it to years with enough paired reporters and refuses a comparison whose
 *  shared set is too thin to call "world". */
export interface LengthOfStayTile {
  value: number;
  yoy: number | null;
  vs2019: number | null;
  year: number | null;
  /** Countries reporting both arrivals and nights in `year`. */
  countries: number | null;
  yoyBasis: AlosBasis | null;
  vs2019Basis: AlosBasis | null;
  /** The reference year's full reporting set, for the coverage-change sentence. */
  referenceCoverage: AlosBasis | null;
  /** `published`: simple average of UN Tourism's published per-country length
   *  of stay (hotels and similar, via Our World in Data); `pooled`: the bulk's
   *  visitor-weighted nights ÷ arrivals. Absent on a body cached before it. */
  method?: "published" | "pooled" | null;
}

export interface GlobalsResponse {
  globalTS: GlobalTSPoint[];
  hero: {
    arrivals: HeroTile;
    receipts: HeroTile;
    spendPerVisitor: HeroTile;
    lengthOfStay: LengthOfStayTile;
  } | null;
  latestYear: number | null;
  priorYear: number | null;
  referenceYear: number | null;
  /** Published source line for `globalTS`, derived server-side from the branch
   *  that actually ran (Euromonitor when an export is loaded, else the UN
   *  Tourism bulk). Optional because the mock fallback in `data.ts` carries no
   *  payload. */
  source?: string;
  /** One label per hero tile — the first three follow `source`, length of stay
   *  is UN Tourism on every branch and names its reporting coverage. */
  sources?: {
    arrivals: string;
    receipts: string;
    spendPerVisitor: string;
    lengthOfStay: string | null;
  };
}

export interface InboundCountry {
  iso: string;
  name: string;
  arrivals: number;
  spend: number;
  /** USD per visitor, latest year; null where either side is unreported. */
  spendPerVisitor: number | null;
  trend: number[];
  /** Receipts over the same window as `trend`, USD billions. */
  spendTrend: number[];
  /** USD per visitor over the same window. */
  spendPerVisitorTrend: number[];
  yoy: number;
  vs2019: number;
  /** 2019→latest arrivals CAGR (server-computed; null when no 2019 base). */
  cagr: number | null;
  spendYoy: number;
  spendVs2019: number;
  spendCagr: number | null;
  spendPerVisitorYoy: number;
  spendPerVisitorVs2019: number;
  spendPerVisitorCagr: number | null;
  latestYear: number;
}

export interface Continent {
  id: string;
  name: string;
  arrivals: number;
  vs19: number;
  year: number;
}

export interface InboundResponse {
  countries: InboundCountry[];
  continents: Continent[];
  latestYear: number | null;
  /** See `GlobalsResponse.source` — same Euromonitor-vs-bulk derivation. */
  source?: string;
  /** The measure the route ranked its growth boards on (default visitors). */
  measure?: InboundMeasureKey;
  /** The year every 2019→latest CAGR compounds from. */
  growthBaseYear?: number;
}

export interface SourceCountry {
  iso: string;
  name: string;
  departures: number;
  /** Which basis `departures` was read on. "Trips abroad" on Euromonitor
   *  Passport (arrivals by country of origin summed over every destination);
   *  on the UN Tourism fallback, "Total visitors" (overnight + same-day) for
   *  most markets and "Overnight only" for the ones that publish no
   *  total-visitor series. Absent on the mock fallback. */
  departuresBasis?: "Total visitors" | "Overnight only" | "Trips abroad";
  spend: number;
  /** USD per outbound trip, latest year; null where either side is unreported. */
  spendPerVisitor?: number | null;
  /** Spend, USD billions, over the 10-year window. */
  trend: number[];
  tripsTrend?: number[];
  spendPerVisitorTrend?: number[];
  /** Spend YoY / vs 2019 (the legacy names). */
  yoy: number;
  vs2019: number;
  tripsYoy?: number;
  tripsVs2019?: number;
  spendPerVisitorYoy?: number;
  spendPerVisitorVs2019?: number;
  /** CAGR of the response's `measure` over its growth window
   *  (`growthBaseYear`→`spendYear`), server-computed; null when the market
   *  reported nothing at one end. */
  cagr: number | null;
  /** Spend / trips in that window's base year. */
  spendBase?: number;
  departuresBase?: number;
  /** Outbound trips (millions) and spend (USD billions) per year — raw
   *  observations, no carry-forward. Let the section switch measure and window
   *  without a refetch. Absent on the mock fallback and on a cached body
   *  published before the controls existed. */
  tripsByYear?: Record<number, number>;
  spendByYear?: Record<number, number>;
  latestYear: number;
}

export interface ContinentSpend {
  id: string;
  name: string;
  spend: number;
  vs19: number;
  year: number;
}

export interface SourceMarketsResponse {
  countries: SourceCountry[];
  continents: ContinentSpend[];
  latestYear: number | null;
  /** See `GlobalsResponse.source` — same Euromonitor-vs-bulk derivation. */
  source?: string;
  /** The measure the route ranked its growth boards on (default visitors). */
  measure?: SourceMeasureKey;
  topBySpendPerVisitor?: SourceCountry[];
  /** Latest year any market reported outbound spend — the endpoint every growth
   *  window is measured back from, so one board never mixes spans. */
  spendYear?: number | null;
  /** Base year of the window the response's `cagr`/`emerging`/`lagging` cover. */
  growthBaseYear?: number | null;
  horizon?: GrowthHorizonKey;
  emerging?: SourceCountry[];
  lagging?: SourceCountry[];
}

export interface CorridorFlow {
  s: string;
  d: string;
  size: number;
  /** 2019→latest CAGR of corridor flow; null when there's no real 2019 base. */
  g: number | null;
}

export interface CorridorsResponse {
  corridors: CorridorFlow[];
  latestYear: number | null;
  source?: string;
}

export interface PurposeCompareRow {
  label: string;
  v19: number;
  v25: number;
}

export type PurposeMix = { business: number; personal: number };

export interface NicheSegment {
  name: string;
  size: string;
  cagr: number;
  note: string;
  source: string;
}

export interface PurposeResponse {
  compare: PurposeCompareRow[];
  byRegion: Record<string, PurposeMix>;
  niches: NicheSegment[];
  latestYear: number | null;
  referenceYear: number | null;
  // Trips with purpose "not specified" — the residual between total arrivals
  // (section 1.1) and personal + business. v19/v25 in millions.
  unspecified?: { v19: number; v25: number };
  // Total arrivals (the 1.1 number) for each year, in millions.
  totalArrivals?: { v19: number; v25: number };
}

export function useGlobals() {
  return useQuery({
    queryKey: ["dashboard", "globals"],
    queryFn: () => get<GlobalsResponse>("/api/v1/dashboard/globals"),
  });
}

export function useInbound() {
  return useQuery({
    queryKey: ["dashboard", "inbound"],
    queryFn: () => get<InboundResponse>("/api/v1/dashboard/inbound"),
  });
}

export function useSourceMarkets() {
  return useQuery({
    queryKey: ["dashboard", "source-markets"],
    queryFn: () => get<SourceMarketsResponse>("/api/v1/dashboard/source-markets"),
  });
}

export function useCorridors() {
  return useQuery({
    queryKey: ["dashboard", "corridors"],
    queryFn: () => get<CorridorsResponse>("/api/v1/dashboard/corridors"),
  });
}

export function usePurpose() {
  return useQuery({
    queryKey: ["dashboard", "purpose"],
    queryFn: () => get<PurposeResponse>("/api/v1/dashboard/purpose"),
  });
}

// --- Saudi · 1.1 demand + 1.2 source markets (Euromonitor inbound, SAU) -----
export interface SaudiDemandResponse {
  visitors2025: number;
  spend2025: number;
  visitors2019: number;
  spend2019: number;
  target2030: number;
  targetSource: "demand_model" | "vision2030";
  year: number;
  baseYear: number;
  edition: number | null;
  timeseries: Array<{ year: number; visits: number; spend: number; spv: number }>;
}

export function useSaudiDemand() {
  return useQuery({
    // The route returns `null` when Euromonitor isn't loaded; the section reads
    // `data ?? mock`, so a null body just means "use the mock fallback".
    queryKey: ["dashboard", "saudi", "demand"],
    queryFn: () => get<SaudiDemandResponse | null>("/api/v1/dashboard/saudi/demand"),
  });
}

export interface SaudiSourceMarketRow {
  iso: string;
  name: string;
  arrivals: number;
  spend: number;
  arrivals2019: number;
  spend2019: number;
  yoy: number;
  shareDelta: number;
  spendByYear: Record<number, number>;
  tripsByYear: Record<number, number>;
}

export interface SaudiSourceMarketsResponse {
  year: number;
  baseYear: number;
  growthBaseYear: number;
  markets: SaudiSourceMarketRow[];
}

export function useSaudiSourceMarkets() {
  return useQuery({
    queryKey: ["dashboard", "saudi", "source-markets"],
    queryFn: () => get<SaudiSourceMarketsResponse>("/api/v1/dashboard/saudi/source-markets"),
  });
}

// --- Saudi · 1.3 destinations (MT Demand Model I5 + national purpose I2) -----
export interface SaudiDestinationRegion {
  id: string;
  name: string;
  visitors: number;
  spend: number;
  /** Prior-year visitors/spend — the YoY (e.g. 2023→2024) denominator. */
  visitorsPrev: number | null;
  spendPrev: number | null;
  avgSpendPerVisitor: number;
  trend: Array<{ year: number; visitors: number }>;
}

export interface SaudiDestinationsNational {
  year: number;
  business: number;
  personal: number;
  purposes: Array<{ key: string; pct: number }>;
}

export interface SaudiDestinationsResponse {
  year: number;
  /** Year the growth metric compares against — `year - 1`, so growth is YoY. */
  prevYear: number | null;
  /** ISO code of the focus country's currency — the deployment's, not a fixed
   *  "SAR": the demo tenant reports its own. */
  currency: string;
  regions: SaudiDestinationRegion[];
  national: SaudiDestinationsNational | null;
}

export function useSaudiDestinations() {
  return useQuery({
    // Null body when the MT Demand Model workbook isn't loaded → section reads
    // `data ?? mock` and keeps the illustrative fallback.
    queryKey: ["dashboard", "saudi", "destinations"],
    queryFn: () => get<SaudiDestinationsResponse | null>("/api/v1/dashboard/saudi/destinations"),
  });
}

// --- Saudi · 3.2.2 accommodation supply (MoT TIC licensed facilities + rooms) -
export interface SaudiAccommodationCategory {
  key: string;
  label: string;
  facilities: number;
  rooms: number;
}

export interface SaudiAccommodationRegion {
  id: string;
  name: string;
  hotels: number;
  facilities: number;
  rooms: number;
  byCategory: SaudiAccommodationCategory[];
}

export interface SaudiAccommodationResponse {
  year: number;
  /** ISO code of the focus country's currency — the deployment's, not a fixed
   *  "SAR": the demo tenant reports its own. */
  currency: string;
  national: {
    hotels: number;
    facilities: number;
    rooms: number;
    byCategory: SaudiAccommodationCategory[];
  };
  regions: SaudiAccommodationRegion[];
}

export function useSaudiAccommodation() {
  return useQuery({
    // Null body when the supply workbooks aren't loaded → 3.2.2 reads
    // `data ?? mock` and keeps the illustrative fallback.
    queryKey: ["dashboard", "saudi", "accommodation"],
    queryFn: () => get<SaudiAccommodationResponse | null>("/api/v1/dashboard/saudi/accommodation"),
  });
}

// --- Saudi · 3.2.1 forward accommodation pipeline (TQ Demand Model) ------------

export interface SaudiPipelineScale {
  /** budget | midscale | upscale | upper-upscale | luxury | others. */
  key: string;
  label: string;
  projects: number;
  keys: number;
  investmentSarBn: number;
}

export interface SaudiPipelineFoldPart {
  label: string;
  projects: number;
  keys: number;
}

export interface SaudiPipelineRegion {
  id: string;
  name: string;
  projects: number;
  keys: number;
  investmentSarBn: number;
  byScale: SaudiPipelineScale[];
  /** Source regions this province sums, when there is more than one. */
  foldedFrom: SaudiPipelineFoldPart[];
}

export interface SaudiPipelineResponse {
  /** ISO code of the focus country's currency — the deployment's, not a fixed
   *  "SAR": the demo tenant reports its own. */
  currency: string;
  openingYears: { min: number | null; max: number | null };
  national: {
    projects: number;
    keys: number;
    investmentSarBn: number;
    byScale: SaudiPipelineScale[];
  };
  regions: SaudiPipelineRegion[];
  /** Projects filed country-wide: counted in `national`, absent from `regions`. */
  unallocated: { projects: number; keys: number; investmentSarBn: number } | null;
}

export function useSaudiPipeline() {
  return useQuery({
    // Null body when the pipeline workbook isn't loaded → 3.2.1 reads
    // `data ?? mock` and keeps the illustrative fallback.
    queryKey: ["dashboard", "saudi", "pipeline"],
    queryFn: () => get<SaudiPipelineResponse | null>("/api/v1/dashboard/saudi/pipeline"),
  });
}

// --- Saudi · 1.4 Actual vs. target (TQ Demand Model Vision-2030 tabs) ----------

export interface SaudiTargetDestination {
  id: string;
  name: string;
  actual: number;
  target: number;
}

export interface SaudiTargetSourceMarket {
  iso: string;
  name: string;
  actual: number;
  target: number;
}

export interface SaudiTargetsResponse {
  actualYear: number;
  targetYear: number;
  destinations: SaudiTargetDestination[];
  sourceMarkets: SaudiTargetSourceMarket[];
}

export function useSaudiTargets() {
  return useQuery({
    // Null body when the target tabs aren't loaded → 1.4 reads `data ?? mock`
    // and keeps the illustrative fallback.
    queryKey: ["dashboard", "saudi", "targets"],
    queryFn: () => get<SaudiTargetsResponse | null>("/api/v1/dashboard/saudi/targets"),
  });
}

// --- Saudi · 3.3 inbound visa & entry (Passport Index, Firecrawl monthly) ------
export interface SaudiVisaMarket {
  iso: string;
  name: string;
  /**
   * PUBLISHED gradient: novisa | evisa | conditional | consular | refused.
   * Derived once, server-side, by `publishedCategory` in `@ow/shared` — never
   * re-derived here, and never the raw Passport Index label.
   */
  cat: string;
  /** Raw Passport Index category, kept for provenance. */
  rawCat?: string;
  voaEligible: boolean;
  /**
   * Off the official list, but a sourced route in exists for travellers holding
   * a qualifying document. Optional: a payload predating the field reads as
   * false, which grades the stricter way rather than claiming a route.
   */
  conditionalRoute?: boolean;
  requirementRaw?: string; // verbatim Passport Index label, e.g. "visa-free90"
  /** On Saudi's official eVisa-eligible list. null until the overlay runs. */
  officialEligible?: boolean | null;
}

export interface SaudiVisaResponse {
  year: number;
  source: string;
  counts: Record<string, number>;
  markets: SaudiVisaMarket[];
  /** Optional for the same reason as `GtmVisaState.rules` — read via `entryRules()`. */
  rules?: SaudiEntryRule[];
  /** Provenance of the eligibility overlay; null until it runs. */
  officialSource?: string | null;
}

export function useSaudiVisa() {
  return useQuery({
    // Null body when the visa table is empty → 3.3 reads `data?.markets ?? mock`
    // and keeps the illustrative fallback.
    queryKey: ["dashboard", "saudi", "visa-entry"],
    queryFn: () => get<SaudiVisaResponse | null>("/api/v1/dashboard/saudi/visa-entry"),
  });
}

// --- Saudi 3.1 Connectivity: seat capacity + direct% (workbook) joined to live
// Aviation Edge route/airport counts. Supply is flat (no YoY) by design. -------
export interface SaudiConnectivityMarket {
  iso3: string;
  name: string;
  // Workbook seat capacity into Saudi, '000s. Null = the market is observed by
  // Aviation Edge but is not in the capacity model — render "not in capacity
  // model", never 0, and skip it in the capacity rankings.
  seats: number | null;
  directPct: number | null; // 0..100
  // Aviation Edge (dated conn_daily) half. Null = NO forward coverage for this
  // market — NOT "zero seats". Render "—", never 0, and skip it in rankings.
  routes: number | null; // distinct direct airport-pairs into Saudi over the window
  fwdSeats: number | null; // forward seats over the dated window (raw count)
  fwdSeatsYoyPct: number | null; // vs the same calendar dates 52 weeks earlier
}
export interface SaudiConnectivityResponse {
  year: number;
  national: {
    totalSeats: number; // '000s
    capacityMarkets: number; // markets the totalSeats sum covers
    directPaxPct: number | null; // 0..100
    activeAirports: number | null;
    intlRoutes: number | null;
  };
  markets: SaudiConnectivityMarket[];
  aviationEdgeLive: boolean;
  /** Days in the dated forward window backing routes/fwdSeats/YoY; 0 when none. */
  windowDays: number;
  /** The Rankings tab's two growth boards, floored by the route on the window's
   *  median forward seats (read at both ends). Absent on a cached body that
   *  predates it — the section then computes the same boards itself. */
  growth?: {
    floor: number;
    ranked: number;
    withYoy: number;
    emerging: SaudiConnectivityGrowthEntry[];
    lagging: SaudiConnectivityGrowthEntry[];
  };
  appliedFilters?: string[];
}
export interface SaudiConnectivityGrowthEntry {
  iso3: string;
  name: string;
  fwdSeats: number;
  fwdSeatsYoyPct: number;
}
export function useSaudiConnectivity() {
  return useQuery({
    // Null body when the workbook isn't loaded → 3.1 keeps its illustrative mock.
    queryKey: ["dashboard", "saudi", "connectivity"],
    queryFn: () => get<SaudiConnectivityResponse | null>("/api/v1/dashboard/saudi/connectivity"),
  });
}

// --- 3.1 supplement: air share of inbound arrivals (UN bulk) -----------
export interface AirShareCountry {
  iso: string;
  name: string;
  year: number;
  airSharePct: number;
  vs2019DeltaPp: number | null;
  totalThousands: number;
  airThousands: number;
}

export interface AirShareResponse {
  countries: AirShareCountry[];
  latestYear: number | null;
}

export function useAirShare() {
  return useQuery({
    queryKey: ["dashboard", "air-share"],
    queryFn: () => get<AirShareResponse>("/api/v1/dashboard/air-share"),
  });
}

// --- 3.1 Connectivity (Aviation Edge: seats / routes / airports) -------
export interface ConnectivityTotals {
  seats: number; // raw seat count (UI formats K/M/B)
  seatsYoyPct: number | null;
  routes: number; // raw country-pair count
  routesYoy: number | null; // delta count
  airports: number; // raw airport count
  /** ISO string — when the global routes sweep last wrote `aviation_routes`.
   *  Optional so the mock fallback in `components/dashboard/data.ts` still
   *  satisfies the type. See repos/connectivity.ts for why it's published. */
  routesRefreshedAt?: string | null;
}

export interface ConnectivityRegion {
  region: string;
  seats: number; // raw seat count over the current live rolling window ("next 23d")
  seatsYoyPct: number | null;
  seatsPriorYear: number | null; // ~1-year-ago window seats ("2025"); null until backfilled
  seatsTwoYear: number | null; // ~2-year-ago window seats ("2024"); null until backfilled
  seatsPrev23d: number | null; // prior 23-day window seats ("last 23 days"); null until ~23d of history
  routes: number;
  routesYoy: number | null;
  airports: number;
}

export interface ConnectivityForwardRow {
  iso: string;
  name: string;
  region: string;
  yoyPct: number | null;
  weeklyAdd: number | null; // raw seats/wk
  partners: string;
}

export interface ConnectivityResponse {
  generatedAt: string;
  periodLabel: string;
  yoyAvailable: boolean;
  // Days of forward data behind the current figures. 'future' = real dated
  // schedules (7/14/21… days from tomorrow; YoY = same N dates last year);
  // 'snapshot' = legacy weighted 23-day snapshot. Optional so a stale server
  // payload (pre-upgrade) still renders.
  windowDays?: number;
  windowSource?: "future" | "snapshot";
  totals: ConnectivityTotals;
  regions: ConnectivityRegion[];
  forwardRanking: ConnectivityForwardRow[];
}

export function useConnectivity() {
  return useQuery({
    queryKey: ["dashboard", "connectivity"],
    queryFn: () => get<ConnectivityResponse>("/api/v1/dashboard/connectivity"),
  });
}

// 3.1.2 — Connectivity deepdive by destination.
export interface ConnectivityDestination {
  iso: string;
  name: string;
  region: string;
  seats: number; // forward seats over the live window (raw; UI formats K/M/B)
  routes: number; // commercial routes into the country
  yoyPct: number | null; // seat-capacity growth vs the year-ago window; null until baseline
}

export interface ConnectivityDestinationsResponse {
  generatedAt: string;
  periodLabel: string;
  yoyAvailable: boolean;
  windowDays?: number;
  windowSource?: "future" | "snapshot";
  destinations: ConnectivityDestination[];
}

export function useConnectivityDestinations() {
  return useQuery({
    queryKey: ["dashboard", "connectivity", "destinations"],
    queryFn: () => get<ConnectivityDestinationsResponse>("/api/v1/dashboard/connectivity/destinations"),
  });
}

// 3.1.3 / 3.1.4 — Connectivity deepdive by country pair / route (airport pair).
// Same payload shape at both grains; aggregated from the flight snapshots, so no
// extra Aviation Edge calls. The frontend builds the chord + A→B lookup from it.
export interface ConnectivityPairNode {
  code: string; // ISO3 (country pairs) or IATA (airport pairs)
  name: string;
  region: string;
  // The node's country, for the flag beside it. Equal to `code` on the country
  // grain; the airport's country on the airport grain. Optional because an L2
  // snapshot cached before the field existed still deserializes.
  iso3?: string | null;
}

export interface ConnectivityPairRow {
  fromCode: string;
  fromName: string;
  toCode: string;
  toName: string;
  seats: number; // forward seats over the live window (raw; UI formats K/M/B)
  routes: number; // distinct nonstop connections (city pairs / carriers)
  yoyPct: number | null; // seat-capacity growth vs the year-ago window; null until baseline
}

export interface ConnectivityPairsResponse {
  generatedAt: string;
  periodLabel: string;
  yoyAvailable: boolean;
  windowDays?: number;
  windowSource?: "future" | "snapshot";
  grain: "country" | "airport";
  nodes: ConnectivityPairNode[];
  pairs: ConnectivityPairRow[];
}

export function useConnectivityCountryPairs() {
  return useQuery({
    queryKey: ["dashboard", "connectivity", "country-pairs"],
    queryFn: () => get<ConnectivityPairsResponse>("/api/v1/dashboard/connectivity/country-pairs"),
  });
}

export function useConnectivityRoutePairs() {
  return useQuery({
    queryKey: ["dashboard", "connectivity", "route-pairs"],
    queryFn: () => get<ConnectivityPairsResponse>("/api/v1/dashboard/connectivity/route-pairs"),
  });
}

// --- 3.2 Affordability (WEF TTDI Price Competitiveness, 1–7) ------------
export interface AffordabilityCountry {
  iso: string;
  name: string;
  region: string;
  score: number; // 1–7; higher = more affordable
  rank: number; // 1 = most affordable
}

export interface AffordabilityResponse {
  countries: AffordabilityCountry[];
  edition: number | null;
  source: string | null;
}

export function useAffordability() {
  return useQuery({
    queryKey: ["dashboard", "affordability"],
    queryFn: () => get<AffordabilityResponse>("/api/v1/dashboard/affordability"),
  });
}

// --- 3.3 Visa & entry (UN Visa Openness Report + manual notes) ---------
export interface VisaEntryCountry {
  iso: string;
  name: string;
  year: number;
  score: number;
  note: string | null;
}

export interface VisaEntryResponse {
  countries: VisaEntryCountry[];
  latestYear: number | null;
}

export function useVisaEntry() {
  return useQuery({
    queryKey: ["dashboard", "visa-entry"],
    queryFn: () => get<VisaEntryResponse>("/api/v1/dashboard/visa-entry"),
  });
}

// --- 4.1 Forecasts (UN T2030 + WTTC EIR + peer benchmarks) -------------
export interface ForecastMilestone {
  slug: string;
  label: string;
  metric: string;
  horizonYear: number;
  value: number;
  lowerBound: number | null;
  upperBound: number | null;
  unit: string;
  source: string;
  note: string | null;
}

export interface PeerForecast {
  organization: string;
  metric: string;
  horizonYear: number;
  value: number;
  unit: string;
  source: string;
  note: string | null;
}

export interface ForecastTrajectory {
  years: number[];
  actual: (number | null)[];
  central: (number | null)[];
  longHorizon: { year: number; arrivalsMillions: number; source: string } | null;
}

export interface ForecastsResponse {
  actual: { year: number; arrivalsMillions: number } | null;
  milestones: ForecastMilestone[];
  peers: PeerForecast[];
  trajectory: ForecastTrajectory;
}

export function useForecasts() {
  return useQuery({
    queryKey: ["dashboard", "forecasts"],
    queryFn: () => get<ForecastsResponse>("/api/v1/dashboard/forecasts"),
  });
}

// --- 2.2 Search signals / 2.4 Booking intent (Google Trends via SerpAPI) ---
// Tile-grid composites per destination. Same shape for both endpoints.
export type SignalPeriod = "3M" | "6M" | "1Y" | "2Y" | "5Y";
export type SignalRegion = "EUROPE" | "MIDDLE_EAST" | "AMERICAS" | "ASIA_PACIFIC" | "AFRICA";

/**
 * How a published growth figure was measured (the server's
 * `search-ladder/periods.ts` `GrowthBasis`).
 *
 * Growth is the change over the period: the mean of the last N full months over
 * the mean of the N months before them, minus one (client definition, 8 Sep
 * 2026). There is no per-month rate.
 *
 *  `period`        the period's two N-month blocks — the normal case. A NULL
 *                  figure on this basis is a coverage gap: the run's window did
 *                  not cover both blocks, which is a statement about the
 *                  measurement, not about the destination.
 *  `first-nonzero` retired with the compound rate; only rows published before
 *                  8 Sep 2026 carry it.
 *  `new`           the prior block read below the precision floor while the
 *                  current one cleared it, so there is nothing to grow FROM. The
 *                  row carries a null growth, stays out of every ranked board,
 *                  and reads "New" — not "—", which would claim the figure is
 *                  unknown when what is known is that the interest is younger
 *                  than the comparison.
 *
 * Optional throughout: a cached body published before the rule landed carries no
 * basis, and those rows were whole-window by construction.
 */
export type GrowthBasis = "period" | "first-nonzero" | "new";

export interface SignalTile {
  iso3: string;
  name: string;
  region: SignalRegion;
  /** Momentum over the selected period, %. NULL when `growthBasis` is `new` —
   *  the destination's search interest is younger than the window, so no rate
   *  exists. Not a 0: the tile, the map and the tracked count keep the
   *  destination and print "New" where the number goes, while every ranked
   *  board, the momentum mean and the scatter skip it. */
  deltaPct: number | null;
  sparkline: number[];
  lowConfidence: boolean;
  /**
   * Relative search volume, 0-100 across all destinations in the selected
   * period (largest = 100). Null until a volume run has been published for that
   * period, in which case 1.2.2 falls back to inbound arrivals as a size proxy.
   */
  relVolume: number | null;
  volumeTier: string | null;
  volumeLowConfidence: boolean;
  /** "published" = deltaPct and relVolume come from the same ladder run. */
  growthSource?: "published" | "own-fetch" | "composite";
  /** See `GrowthBasis`. `new` is why `deltaPct` is null. */
  growthBasis?: GrowthBasis;
  /** For a `new` row, the month its interest began ("Mar 2026") — the first
   *  non-zero month of the period's current block. The map card reads
   *  "Data available from <this>" where a rate would go; the ranked
   *  tables keep the bare word "New". Null on every row with a rate. */
  dataFromMonth?: string | null;
}

export interface SignalTileResponse {
  period: SignalPeriod;
  refreshedAt: string | null;
  tiles: SignalTile[];
}

export function useSearchSignals(period: SignalPeriod) {
  return useQuery({
    queryKey: ["dashboard", "search-signals", period],
    queryFn: () => get<SignalTileResponse>(`/api/v1/dashboard/search-signals?period=${period}`),
    staleTime: 60 * 60 * 1000,
  });
}

// 2.2 — Sentiment. Per-destination composite reception + arrivals, fanned in
// from Reddit + Google News and scored by Bedrock. Powers all four views: the
// landscape scatter + love-vs-visit (rows), the movers (rows w/ delta30+themes),
// and the source-market matrix (Google News country editions — a media lens).
export interface SentimentApiRow {
  iso3: string;
  name: string;
  region: "Europe" | "Asia Pacific" | "Middle East" | "Americas" | "Africa";
  arrivals: number;
  arrR: number;
  score: number;
  volume: number;
  delta30?: number;
  themes: string[];
  sources: Array<{ source: "reddit" | "news"; score: number; volume: number }>;
  lowConfidence: boolean;
}

export interface SentimentMatrix {
  rows: string[]; // destination iso3s
  cols: string[]; // source-market iso3s
  cells: Record<string, Record<string, number>>;
}

export interface SentimentResponse {
  refreshedAt: string | null;
  rows: SentimentApiRow[];
  matrix: SentimentMatrix | null;
}

export function useSentiment() {
  return useQuery({
    queryKey: ["dashboard", "sentiment"],
    queryFn: () => get<SentimentResponse>("/api/v1/dashboard/sentiment"),
    staleTime: 60 * 60 * 1000,
  });
}

export function useBookingIntent(period: SignalPeriod) {
  return useQuery({
    queryKey: ["dashboard", "booking-intent", period],
    queryFn: () => get<SignalTileResponse>(`/api/v1/dashboard/booking-intent?period=${period}`),
    staleTime: 60 * 60 * 1000,
  });
}

// 2.1 / 2.3 — top origin markets per destination, for the map + list hover
// popup. One `track`, keyed by destination iso3, cleaned to real foreign
// origins (self + micro-geo noise dropped server-side). See repos/geo-origins.ts.
export interface SignalOrigin {
  iso3: string;
  iso2: string;
  name: string;
  /** Google Trends 0–100 index within this destination's own query. */
  value: number;
  /** Momentum mode only: this origin's growth over the response's `period` —
   *  the mean of its last N months against the mean of the N months before
   *  them, in %. Always a real %, never a "new" placeholder — origins the
   *  ladder could not measure are dropped server-side. */
  growthPct?: number;
  /** Momentum mode only: this destination only has a card because the server's
   *  rounding-noise floor was waived for it — its foreign origins all score 1-2
   *  on the raw index, so the percentages are real arithmetic on a very small
   *  base. The popup renders it like any other momentum card; the flag is here so
   *  anything that quotes these figures can tell them apart. */
  lowBase?: boolean;
  /** Momentum mode only, paired-ladder rows only: see `GrowthBasis`. Never
   *  `new` — an origin with no rate is dropped server-side. */
  growthBasis?: GrowthBasis;
}
export type SignalOriginsMode = "volume" | "momentum";
export interface SignalOriginsResponse {
  track: "search" | "booking";
  period: SignalPeriod;
  mode: SignalOriginsMode;
  refreshedAt: string | null;
  originsByIso: Record<string, SignalOrigin[]>;
}

// Both modes follow the period selector: volume ranks the period's biggest
// origins, momentum ranks its share growth against the prior window. All five
// periods now have a scraped prior pie, so momentum resolves to the period asked
// for; the server's 1Y fallback survives only as a safety net, and the response
// reports the window it actually used. Keyed on (mode, period).
function useSignalOrigins(track: "search" | "booking", period: SignalPeriod, mode: SignalOriginsMode = "volume") {
  const path = track === "search" ? "search-signals/origins" : "booking-intent/origins";
  const query = mode === "momentum" ? `?mode=momentum&period=${period}` : `?period=${period}`;
  return useQuery({
    queryKey: ["dashboard", track, "origins", mode, period],
    queryFn: () => get<SignalOriginsResponse>(`/api/v1/dashboard/${path}${query}`),
    staleTime: 60 * 60 * 1000,
  });
}
export const useSearchOrigins = (period: SignalPeriod) => useSignalOrigins("search", period);
export const useBookingOrigins = (period: SignalPeriod) => useSignalOrigins("booking", period);
// Momentum follows the selected period too: the server returns that period's
// share growth against its own prior window. Consumers should label the popup
// from the response's `period`, not the selector — they differ if a prior pie
// ever goes missing and the server falls back to 1Y.
export const useSearchOriginsMomentum = (period: SignalPeriod) => useSignalOrigins("search", period, "momentum");
export const useBookingOriginsMomentum = (period: SignalPeriod) => useSignalOrigins("booking", period, "momentum");

// Saudi 2.1 — per-source-market Google Trends about Saudi. Three reads in one
// response: source-market momentum into Saudi, the worldwide
// religious/leisure/events purpose split, and the emerging-query radar.
/** How the published volume column was derived. `weighted` = cross-market
 *  relative volume (search intensity x internet users, normalized to 100 for the
 *  top market); `legacy` = no ladder has run yet, so there is no volume at all
 *  and momentum is per-market Google Trends. The board's column header and note
 *  are chosen from THIS, never from the field name.
 *
 *  `intensity-only` is gone with the weighting gate (2026-09-03): the gate
 *  validated weighted volume against Euromonitor arrivals and withheld it when
 *  the correlation failed, which published a per-head number under a size
 *  heading. Arrivals play no part in this measurement, so there is nothing to
 *  validate against and the weighted figure is always published. */
export type SaudiVolumeMode = "weighted" | "legacy";

export interface SaudiMarketMomentum {
  iso3: string;
  name: string;
  /** Null when the window has no readable endpoint — NOT 0. The boards print
   *  "—" and rank it last. */
  deltaPct: number | null;
  sparkline: number[];
  lowConfidence: boolean;
  /** Cross-market weighted volume on the paired-geo anchor ladder, 0–100.
   *  Null only when the market has no internet-user weight, or no ladder has run. */
  relVolume: number | null;
  /** Unweighted per-head interest on the same ladder scale. Published alongside
   *  the weighted figure; the board shows the weighted one. */
  intensity: number | null;
  /** Internet-user weighting warning (CHN/RUS/KOR); footnoted on the board. */
  weightCaveat: string | null;
  /** See `GrowthBasis`. `new` is why `deltaPct` is null. */
  growthBasis?: GrowthBasis;
  /** For a `new` row, the month its interest began ("Mar 2026") — the first
   *  non-zero month of the period's current block. The map card reads
   *  "Data available from <this>" where a rate would go; the ranked
   *  tables keep the bare word "New". Null on every row with a rate. */
  dataFromMonth?: string | null;
}

export interface SaudiPurposeSplit {
  key: "religious" | "leisure" | "events";
  label: string;
  deltaPct: number;
  sharePct: number;
  sparkline: number[];
}

export interface SaudiEmergingTerm {
  term: string;
  /** Corpus category — the card's chip (destination · event · experience …). */
  bucket: string;
  /** Relative volume on the anchor-ladder scale (0–100, ordinal). */
  score: number | null;
  tier: string;
  trendClass: string;
  breakout: boolean;
  /** Momentum as a fraction (0.5 = +50%): windowed to the selected period when a
   *  5-year series exists. Null when not measurable — never the 13w snapshot. */
  momentum90: number | null;
  /** True when momentum90 reflects the selected period, not the snapshot. */
  periodWindowed?: boolean;
  /** Months of real search history behind the term (its 5-year series minus the
   *  pre-launch zero padding). When `momentum90` is null and this is set, the term
   *  is too new for the selected period and the board publishes this instead of a %. */
  historyMonths?: number | null;
  /** Month the term first registers on Google Trends ("Jul 2025"). */
  firstMeasurable?: string | null;
  /** Set when the momentum rests on part of its baseline window (empty months are
   *  excluded from the baseline): "baseline 1.3, from 1 of 12 months". */
  baselineBasis?: string | null;
  /** Last 52w vs prior 52w (fraction); null when not measurable. */
  yoy: number | null;
  /** Last-12-month monthly values for the sparkline. */
  spark: number[];
  /** Top countries by relative interest, e.g. ["AU","NZ","US"]. */
  drivenBy: string[];
  /** Card one-liner. */
  read: string;
}

export interface SaudiSearchResponse {
  period: SignalPeriod;
  refreshedAt: string | null;
  /** Absent on a cached body published before the ladder shipped — the section
   *  falls back to "legacy" and withholds the volume board rather than labelling
   *  an unknown column. */
  volumeMode?: SaudiVolumeMode;
  markets: SaudiMarketMomentum[];
  purposes: SaudiPurposeSplit[];
  emerging: SaudiEmergingTerm[];
}

export function useSaudiViral(period: SignalPeriod) {
  return useQuery({
    queryKey: ["dashboard", "saudi-viral", period],
    queryFn: () =>
      get<Pick<SaudiSearchResponse, "period" | "emerging">>(`/api/v1/dashboard/saudi/viral?period=${period}`),
    staleTime: 60 * 60 * 1000,
  });
}

export function useSaudiSearchSignals(period: SignalPeriod) {
  return useQuery({
    queryKey: ["dashboard", "saudi-search-signals", period],
    queryFn: () => get<SaudiSearchResponse>(`/api/v1/dashboard/saudi/search-signals?period=${period}`),
    staleTime: 60 * 60 * 1000,
  });
}

// Saudi 2.2 — per-source-market BOOKING-stage Google Trends about Saudi. One
// response carries four reads: per-market booking momentum (01), interest→intent
// conversion with visa/air attribution (02), the near-term demand-weighted lead
// signal (03), and the auto route-dev / policy action list (04).
export interface SaudiBookingMomentum {
  iso3: string;
  name: string;
  /** Null when the window has no readable endpoint — NOT 0. */
  deltaPct: number | null;
  sparkline: number[];
  lowConfidence: boolean;
  /** The booking twin of 2.1's volume columns; see `SaudiVolumeMode`. */
  relVolume: number | null;
  intensity: number | null;
  weightCaveat: string | null;
  /** See `GrowthBasis`. `new` is why `deltaPct` is null. */
  growthBasis?: GrowthBasis;
  /** For a `new` row, the month its interest began ("Mar 2026") — the first
   *  non-zero month of the period's current block. The map card reads
   *  "Data available from <this>" where a rate would go; the ranked
   *  tables keep the bare word "New". Null on every row with a rate. */
  dataFromMonth?: string | null;
}

export type SaudiConversionStatus = "converting" | "curious" | "cooling" | "steady";

export interface SaudiVisaFriction {
  cat: string;
  label: string;
  friction: number; // 0 (easy) … 3 (hard)
}

export interface SaudiAirCapacity {
  seats: number; // '000s/yr capacity into Saudi
  directPct: number | null;
  thin: boolean;
}

export interface SaudiConversionMarket {
  iso3: string;
  name: string;
  searchMomentum: number | null;
  bookingMomentum: number;
  gap: number | null;
  status: SaudiConversionStatus;
  visa: SaudiVisaFriction | null;
  air: SaudiAirCapacity | null;
  bottleneck: "visa" | "seats" | "both" | null;
  lowConfidence: boolean;
}

export interface SaudiLeadContributor {
  iso3: string;
  name: string;
  bookingMomentum: number;
  weightPct: number;
}

export interface SaudiBookingLead {
  compositeMomentum: number;
  leadWindowDays: string;
  marketsCovered: number;
  contributors: SaudiLeadContributor[];
}

export interface SaudiBookingAction {
  iso3: string;
  name: string;
  intentMomentum: number;
  bottleneck: "thin_seats" | "hard_visa";
  kind: "route" | "policy";
  detail: string;
  priority: number;
}

export interface SaudiBookingPosition {
  iso3: string;
  name: string;
  region: string;
  arrivals: number; // inbound travellers to Saudi (millions) — X axis
  bookingMomentum: number; // % vs. prior period — Y axis
  lowConfidence: boolean;
}

export interface SaudiBookingResponse {
  period: SignalPeriod;
  refreshedAt: string | null;
  volumeMode?: SaudiVolumeMode;
  markets: SaudiBookingMomentum[];
  conversion: SaudiConversionMarket[];
  lead: SaudiBookingLead | null;
  actions: SaudiBookingAction[];
  positioning: SaudiBookingPosition[];
}

export function useSaudiBookingIntent(period: SignalPeriod) {
  return useQuery({
    queryKey: ["dashboard", "saudi-booking-intent", period],
    queryFn: () => get<SaudiBookingResponse>(`/api/v1/dashboard/saudi/booking-intent?period=${period}`),
    staleTime: 60 * 60 * 1000,
  });
}

export type EmergingBucket = "behavior" | "narrative";

export interface EmergingSearchTerm {
  term: string;
  bucket: EmergingBucket;
  /** Growth over the selected period, %. Null when the term carries a series but
   *  the period cannot be measured for it — the board drops the row. */
  growth: number | null;
  historyMonths?: number | null;
  firstMeasurable?: string | null;
  markets: string[];
  note: string;
  countryCount: number;
  totalVolume: number;
  // Last 12 monthly points, oldest → newest — the same shape as
  // `TrendPulseCard.spark`, so the "Emerging terms" board draws the same
  // 12-month trend in its drill-down as "What's going viral". Empty for legacy
  // Trending-Now rows (no stored series); the popup omits the block rather than
  // drawing a flat line.
  spark: number[];
}

export interface EmergingSearchesResponse {
  bucket: EmergingBucket;
  terms: EmergingSearchTerm[];
  generatedAt: string | null;
  source: string | null;
  // Present when a period was requested; periodWindowed is true once the data
  // carries a 5-year series (anchor-ladder rows) so growth varies by period.
  period?: SignalPeriod | null;
  periodWindowed?: boolean;
}

// Google Trends "Trending Now" (Travel) aggregated across geos, split by
// bucket. Refreshed daily by the server cron; empty `terms` until first
// run.
//   2.1 "What's going viral" → behavior  (verbs / formats, no place)
//   2.2 "What's emerging"    → narrative (place + story)
export function useViralBehaviors() {
  return useQuery({
    queryKey: ["dashboard", "emerging-searches", "behavior"],
    queryFn: () => get<EmergingSearchesResponse>("/api/v1/dashboard/emerging-searches?bucket=behavior"),
  });
}

export function useEmergingNarratives(period: SignalPeriod) {
  return useQuery({
    queryKey: ["dashboard", "emerging-searches", "narrative", period],
    queryFn: () =>
      get<EmergingSearchesResponse>(`/api/v1/dashboard/emerging-searches?bucket=narrative&period=${period}`),
  });
}

/** Section 1.1 tab 01 "By volume" — the SAME corpus and the same run as
 *  `useEmergingNarratives` above, ranked by period-scaled relative search volume
 *  instead of growth, and unfiltered (a big style that is cooling still belongs
 *  on a volume board). One measurement behind both tabs, so a term cannot carry
 *  two different numbers on two tabs of one section. */
export function useViralByVolume(period: SignalPeriod) {
  return useQuery({
    queryKey: ["dashboard", "emerging-searches", "narrative", "volume", period],
    queryFn: () =>
      get<EmergingSearchesResponse>(
        `/api/v1/dashboard/emerging-searches?bucket=narrative&period=${period}&rank=volume`,
      ),
  });
}

// --- 2.1 "What's going viral" — report-sourced behavioral trends -----------
// Replaces the Google Trends behavior bucket for this panel. Verified, cited
// trend points extracted from travel-industry reporting; human-reviewed before
// they publish. Quarterly refresh.

export interface BehavioralTrend {
  id: string;
  label: string;
  insight: string;
  stat: string | null;
  publisher: string;
  sourceTitle: string;
  sourceUrl: string;
  sourceDate: string | null;
  quarter: string;
  status: string;
  /** Admin-uploaded hero image (versioned URL), or null to use the stock photo. */
  imageUrl: string | null;
}

export interface BehavioralTrendsResponse {
  trends: BehavioralTrend[];
  generatedAt: string | null;
  source: string | null;
}

/** Public panel — auto-published, LLM-extracted trends. */
export function useBehavioralTrends() {
  return useQuery({
    queryKey: ["dashboard", "behavioral-trends"],
    queryFn: () => get<BehavioralTrendsResponse>("/api/v1/dashboard/behavioral-trends"),
  });
}

// "The biggest shifts" — significant quantified behavioral changes from the
// same reporting, ranked by magnitude.
export interface BehavioralShift {
  behavior: string;
  change: string; // headline change figure, e.g. "+77%"
  periodFrom: string; // baseline period of the comparison, e.g. "2019"
  periodTo: string; // current period compared to, e.g. "2025"
  mag: number;
  dir: "up" | "down";
  impl: string;
  src: string;
  sourceTitle: string;
}

export interface BehavioralShiftsResponse {
  shifts: BehavioralShift[];
  generatedAt: string | null;
  source: string | null;
}

export function useBehavioralShifts() {
  return useQuery({
    queryKey: ["dashboard", "behavioral-shifts"],
    queryFn: () => get<BehavioralShiftsResponse>("/api/v1/dashboard/behavioral-shifts"),
  });
}

// 2.x "Trend pulse" — uploaded relative-ranking + trend-health workbook.
// track 'a' = behaviors going viral, 'b' = emerging destination narratives.
export interface TrendPulseCard {
  rank: number;
  term: string;
  score: number | null; // relative volume (ordinal across terms)
  tier: string;
  trendClass: string; // Rising | Stable | Fading | Dead/Trace
  breakout: boolean;
  yoy: number | null;
  yoyPreShift: number | null;
  shiftSuspect: boolean;
  momentum90: number | null; // post-shift snapshot, OR windowed to `period` when requested
  periodWindowed?: boolean; // true when momentum90 reflects the requested period, not the snapshot
  // Months of real search history (5-year series minus pre-launch zero padding).
  // momentum90 null + this set = too new for the requested period → the board
  // prints "NEW · N months of history" rather than a differently-windowed %.
  historyMonths?: number | null;
  // Month the term first registers on Google Trends ("Jul 2025").
  firstMeasurable?: string | null;
  // Set when the momentum rests on part of its baseline window: the empty months
  // are excluded from the baseline, so the card marks the % and states the basis.
  baselineBasis?: string | null;
  bucket: string | null;
  region: string | null;
  drivenBy: string[]; // top country codes
  spark: number[]; // last-12-month monthly values
  read: string; // card one-liner
}

export interface TrendPulseResponse {
  track: string;
  trackLabel: string | null;
  source: string | null;
  process: string | null;
  generatedAt: string | null;
  uploadedAt: string | null;
  fileName: string | null;
  period?: SignalPeriod | null; // the period the momentum reflects (null = snapshot)
  periodWindowed?: boolean; // true when any card's momentum was windowed to `period`
  cards: TrendPulseCard[];
}

// track 'a' = behaviors going viral, 'b' = emerging destination narratives.
// Pass `period` to window each card's momentum to it (the "What's going viral"
// board's time-period selector); omit it only when no period was requested.
export function useTrendPulse(track: "a" | "b", period?: SignalPeriod) {
  return useQuery({
    queryKey: ["dashboard", "trend-pulse", track, period ?? null],
    queryFn: () =>
      get<TrendPulseResponse>(`/api/v1/dashboard/trend-pulse?track=${track}${period ? `&period=${period}` : ""}`),
  });
}

// --- Go to Market · Market Brief & Performance (Plan 18) ---------------------
// A per-source-market → Saudi brief, composed server-side from existing
// dashboard data. Panels are nullable: the section renders only the cards the
// selected market can back with real numbers.

export interface GtmMarketOption {
  iso: string;
  name: string;
}

export interface GtmRankMove {
  name: string;
  from: number;
  to: number;
}

export interface GtmGlance {
  tier: string;
  tierNote: string | null;
  priority: boolean;
  rank: number;
  totalMarkets: number;
  rank2019: number | null;
  climbedRanks: number | null;
  sharePctVisitors: number;
  sharePctSpend: number;
  surpassed: GtmRankMove[];
  overtakenBy: GtmRankMove[];
}

export interface GtmToday {
  year: number;
  baseYear: number;
  visitorsM: number;
  spendUsdB: number;
  spendPerVisitorUsd: number;
  visitorsCagrPct: number | null;
  spendCagrPct: number | null;
  spvCagrPct: number | null;
  baseVisitorsM: number | null;
  baseSpendUsdB: number | null;
}

export interface GtmTargets {
  actualYear: number;
  targetYear: number;
  actualM: number;
  targetM: number;
  realizationPct: number;
}

export interface GtmOutbound {
  sharePct: number;
  outboundM: number | null;
  momentumPct: number | null;
}

export interface GtmInterestTheme {
  term: string;
  value: number;
  isBreakout: boolean;
  note: string | null;
}

export interface GtmInterest {
  themes: GtmInterestTheme[];
}

export interface GtmBenchRow {
  iso3: string;
  name: string;
  from: number | null;
  to: number;
  delta: number | null;
  isSaudi: boolean;
}

export interface GtmBenchmarks {
  title: string;
  baseYear: number;
  latestYear: number;
  saudiRank: number;
  saudiRank2019: number | null;
  passed: string[];
  rows: GtmBenchRow[];
  note: string | null;
}

export interface GtmMarketBrief {
  iso: string;
  name: string;
  glance: GtmGlance | null;
  today: GtmToday | null;
  targets: GtmTargets | null;
  outbound: GtmOutbound | null;
  benchmarks: GtmBenchmarks | null;
  interest: GtmInterest | null;
}

export const gtmMarketsQuery = () => ({
  queryKey: ["dashboard", "gtm", "markets"] as const,
  queryFn: () => get<{ markets: GtmMarketOption[] }>("/api/v1/dashboard/gtm/markets"),
});

export function useGtmMarkets() {
  return useQuery(gtmMarketsQuery());
}

export const gtmMarketBriefKey = (iso: string | null) => ["dashboard", "gtm", "market-brief", iso] as const;

export function useGtmMarketBrief(iso: string | null) {
  return useQuery({
    queryKey: gtmMarketBriefKey(iso),
    queryFn: () => get<GtmMarketBrief>(`/api/v1/dashboard/gtm/market-brief?iso=${iso}`),
    enabled: !!iso,
  });
}

/** Download URL for the Excel export of a market's brief. */
export function gtmBriefExportUrl(iso: string): string {
  return `/api/v1/dashboard/gtm/market-brief/export?iso=${encodeURIComponent(iso)}`;
}

/** Download URL for the server-rendered PDF of a market's brief. */
export function gtmBriefPdfUrl(iso: string): string {
  return `/api/v1/dashboard/gtm/market-brief/pdf?iso=${encodeURIComponent(iso)}`;
}

// GTM · Connectivity (Plan 20). The air-bridge playbook for one source market →
// Saudi. Every panel is nullable — the frontend renders only what the data backs.
export interface GtmConnBridge {
  seatsK: number;
  directPct: number | null;
  rankByCapacity: number;
  totalMarkets: number;
  sharePctCapacity: number;
  aviationEdgeLive: boolean;
  routes: number | null;
  fwdSeats: number | null;
  fwdSeatsYoyPct: number | null;
  national: {
    totalSeatsK: number;
    directPaxPct: number | null;
    activeAirports: number | null;
    intlRoutes: number | null;
  };
}

export type GtmGapVerdict = "under-served" | "balanced" | "over-served";

export interface GtmConnGap {
  demandSharePct: number;
  capacitySharePct: number;
  index: number | null;
  verdict: GtmGapVerdict;
  directPct: number | null;
}

export interface GtmConnRoute {
  depIata: string;
  arrIata: string;
  seats: number;
  carriers: number;
}

export interface GtmConnRoutes {
  routes: GtmConnRoute[];
  totalSeats: number;
}

export interface GtmConnRouteCapacityGap {
  depIata: string;
  depName: string | null;
  depLabel: string;
  arrIata: string;
  arrName: string | null;
  arrLabel: string;
  targetVisitors2026: number | null;
  targetVisitorsSource: string;
  targetDirectVisitors2026: number | null;
  scheduledSeats2025: number;
  scheduledSeatsSource: string;
  directFlights2025: number | null;
  averageSeatsPerFlight: number | null;
  monthlyCapacity2025: GtmConnRouteMonthlyCapacity[];
  genuineAvailableSeats: number | null;
  gapSeats: number | null;
  gapFlights: number | null;
  gapFlightsPerWeek: number | null;
  isDirect: boolean;
  served: boolean;
  demandBasis: "maintained" | "none";
}

export interface GtmConnRouteMonthlyCapacity {
  month: number;
  scheduledSeats: number;
  availableSeats: number | null;
}

export interface GtmConnRouteCapacityGaps {
  routes: GtmConnRouteCapacityGap[];
  totalRoutes: number;
  servedRoutes: number;
  demandSeededRoutes: number;
  grid: {
    originAirports: number;
    focusAirports: number;
    originBasis: string;
    focusBasis: string;
    strategicGateways: string[];
  };
  demandSource: string;
  scheduledSeatsSource: string;
  notes: string[];
  maxShortageSeats?: number;
}

export interface GtmConnMonthlyDistribution {
  year: number;
  month: number;
  label: string;
  tourists: number;
  share: number;
  sourceFile: string;
  sourceSheet: string;
}

export interface GtmConnRouteSeasonalityCell {
  month: number;
  label: string;
  demandSeats: number;
  availableSeats: number;
  deficitSeats: number;
  intensity: number;
}

export interface GtmConnRouteSeasonalityRow {
  route: string;
  depLabel: string;
  arrLabel: string;
  gapSeats: number;
  diagnosis: "structural" | "seasonal";
  peakLabel: string;
  monthly: GtmConnRouteSeasonalityCell[];
}

export interface GtmConnRouteSeasonality {
  year: number;
  months: GtmConnMonthlyDistribution[];
  routes: GtmConnRouteSeasonalityRow[];
  structuralCount: number;
  seasonalCount: number;
  source: string;
  notes: string[];
}

export interface GtmConnGapRouteAsk {
  route: string;
  depLabel: string;
  arrLabel: string;
  kind: "new" | "existing";
  shortfallSeats: number;
  diagnosis: "structural" | "seasonal" | null;
  peakLabel: string | null;
}

export interface GtmConnCapacityGapYear {
  year: number;
  targetVisitors: number;
  targetDirectVisitors: number | null;
  availableSeats: number | null;
  gapSeats: number | null;
  newRouteSeats: number | null;
  existingRouteSeats: number | null;
  surplusSeats: number | null;
  unmodelledSeats: number | null;
  routeAsks: GtmConnGapRouteAsk[];
}

export interface GtmConnCapacityGap {
  years: GtmConnCapacityGapYear[];
  targetSource: string;
  availableSeatsSource: string;
  notes: string[];
}

export interface GtmConnFormulaSource {
  field: string;
  source: string;
}

export interface GtmConnFormula {
  haul: string | null;
  loadFactor: number | null;
  targetVisitors2026: number | null;
  targetVisitorsSource: string;
  airArrivalShare2026: number | null;
  targetAirArrivalVisitors2026: number | null;
  directFlightShare2026: number | null;
  targetDirectVisitors2026: number | null;
  scheduledSeats2025: number | null;
  scheduledSeatsSource: string;
  seatsFilled2025: number | null;
  passengersCarried2025: number | null;
  saudiResidentVisitors2026: number | null;
  saudiResidentAirVisitors2026: number | null;
  connectingPassengers2026: number | null;
  genuineAvailableSeats2026: number | null;
  seatDemand2026: number | null;
  paxDemand2026: number | null;
  directPaxDemand2026: number | null;
  formulaDirectPct: number | null;
  transferPct: number | null;
  outboundPct: number | null;
  inboundDirectPax2026: number | null;
  indirectDemandPax2026: number | null;
  shortageCurrentLf2026: number | null;
  shortageTargetLf2026: number | null;
  gapSeatsCurrentLf: number | null;
  gapSeatsTargetLf: number | null;
  averageSeatsPerFlight: number | null;
  planeSeatsAssumption: number | null;
  gapFlightsPerWeek: number | null;
  spendAtRiskCurrentLf2026: number | null;
  spendAtRiskTargetLf2026: number | null;
  inboundSpt2026: number | null;
  sources: GtmConnFormulaSource[];
}

export interface GtmGeo {
  lat: number;
  lon: number;
}

export interface FlightTimingFlight {
  flightId: string;
  airlineIata: string;
  depIata: string;
  arrIata: string;
  depTimeLocal: string;
  estArrTimeLocal: string | null;
  seats: number;
  weeklyFrequency: number;
  score: number;
  rating: "high" | "medium" | "low";
  reason: string;
}

export interface FlightTimingPair {
  depCity: string;
  arrCity: string;
  score: number;
  verdict: "high" | "medium" | "low";
  flights: FlightTimingFlight[];
}

export interface FlightTimingPanel {
  countryScore: number;
  verdict: string;
  flightsAssessed: number;
  friendlyPct: number;
  lowCount: number;
  snapshotDate: string;
  notes: string[];
  pairs: FlightTimingPair[];
}

export interface GtmConnectivity {
  iso: string;
  name: string;
  origin: GtmGeo | null;
  bridge: GtmConnBridge | null;
  gap: GtmConnGap | null;
  routes: GtmConnRoutes | null;
  routeGaps: GtmConnRouteCapacityGaps | null;
  routeSeasonality: GtmConnRouteSeasonality | null;
  capacityGap: GtmConnCapacityGap | null;
  formula: GtmConnFormula | null;
  flightTiming: FlightTimingPanel | null;
  priorities: null;
  opportunities: null;
}

export const gtmConnectivityKey = (iso: string | null) => ["dashboard", "gtm", "connectivity", iso] as const;

export function useGtmConnectivity(iso: string | null) {
  return useQuery({
    queryKey: gtmConnectivityKey(iso),
    queryFn: () => get<GtmConnectivity>(`/api/v1/dashboard/gtm/connectivity?iso=${iso}`),
    enabled: !!iso,
  });
}

/** Download URL for the Excel export of a market's connectivity playbook. */
export function gtmConnectivityExportUrl(iso: string): string {
  return `/api/v1/dashboard/gtm/connectivity/export?iso=${encodeURIComponent(iso)}`;
}

/** Download URL for the server-rendered PDF of a market's connectivity playbook. */
export function gtmConnectivityPdfUrl(iso: string): string {
  return `/api/v1/dashboard/gtm/connectivity/pdf?iso=${encodeURIComponent(iso)}`;
}

// GTM · Distribution. Served from the scraped gtm_dist_* tables (agent-fleet
// collection, adversarially verified). Markets without scraped rows return the
// typed missing-source state; unobserved metrics are null end-to-end.
export interface GtmDistributionRequiredSource {
  key: string;
  label: string;
  status: "missing" | "loaded";
  detail: string;
}

export type GtmDistCoverageStatus = "none" | "crit" | "lim" | "ok" | "na";

export interface GtmDistPlatformRow {
  distributor: string;
  domain: string;
  type: string;
  accessible: boolean;
  saudiTotal: number | null;
  saudiReligious: number | null;
  saudiLeisure: number | null;
  benchmarks: (number | null)[];
  lowerBound: boolean;
  /** Per-cell lower-bound marks (optional: absent in the mock fallback). */
  saudiLowerBound?: boolean;
  benchmarkLowerBounds?: boolean[];
  status: GtmDistCoverageStatus;
  evidence: { metric: string; url: string; note?: string }[];
  method: string;
  notes: string | null;
}

export interface GtmDistPlatformPresence {
  benchmarks: string[];
  types: string[];
  rows: GtmDistPlatformRow[];
  gapCount: number;
}

export interface GtmDistCoverageRow {
  distributor: string;
  domain: string;
  cells: (number | null)[];
}

export interface GtmDistListingCoverage {
  regions: string[];
  productTypes: string[];
  regionRows: GtmDistCoverageRow[];
  productTypeRows: GtmDistCoverageRow[];
  /** Total SAU items backing these matrices for this market. 0 = legacy jsonb-derived mode. */
  itemsSampled?: number;
}

export interface GtmDistFitExample {
  title: string;
  url: string;
  distributor: string;
  nights: number | null;
  stars: number | null;
  theme: string | null;
  priceUsd: number | null;
}

export interface GtmDistProductFit {
  sampleCount: number;
  medianNights: number | null;
  shortStayShare: number | null;
  religiousShare: number | null;
  starMix: { label: string; count: number }[];
  themeMix: { theme: string; count: number }[];
  medianPriceUsd: number | null;
  pricedSampleCount: number;
  examples: GtmDistFitExample[];
}

export interface GtmDistPricingMatrix {
  destinations: string[];
  /** Count of leading Saudi columns — 1 (legacy) or 2 (religious/leisure split). */
  saudiColumnCount: number;
  archetypes: string[];
  matrix: {
    archetype: string;
    values: (number | null)[];
    saudiIndex: (number | null)[];
    sampleCounts: number[];
  }[];
  overall: { values: (number | null)[]; saudiIndex: (number | null)[] };
  sampleUrls: Record<string, { title: string; url: string; priceUSD: number }[]>;
}

export interface GtmDistPlaybookItem {
  n: number;
  title: string;
  detail: string;
}

// Item-level "travel experiences" inventory — destination-side data, same for
// every source market. Null when the active run has no experience rows.
export interface GtmDistExperiencePlatformTotals {
  platform: string;
  totals: Record<string, { platformTotal: number | null; captured: number }>;
}

export interface GtmDistExperienceItem {
  platform: string;
  name: string;
  country: string;
  city: string | null;
  location: string | null;
  priceUSD: number | null;
  priceLocal: { amount: number; currency: string } | null;
  url: string;
}

export interface GtmDistExperiences {
  platforms: GtmDistExperiencePlatformTotals[];
  itemCount: number;
}

export interface GtmDistMeta {
  fetchedAt: string | null;
  source: string;
  process: string;
  distributorCount: number;
  /** Mirrors the server's `GtmDistMeta["verification"]` — every sampled claim in
   *  exactly one bucket, so `checked` equals the sum of the rest. */
  verification: {
    checked: number;
    confirmed: number;
    corrected: number;
    filled: number;
    close: number;
    refuted: number;
    unverifiable: number;
    other: number;
  } | null;
}

// Distribution · 03 "Package Assessment" — package–demand match. Every number is
// computed server-side to the developer brief's formulas; the panel only formats.
export type GtmPackageGapFlag = "under" | "matched" | "over";

export interface GtmPackageChannel {
  key: string;
  label: string;
  packages: number;
  score: number | null;
}

export interface GtmPackageRow {
  rank: number;
  activity: string;
  short: string;
  /** % of the market's travelers citing the activity — a % of PEOPLE. */
  pref: number;
  /** % of each channel's packages featuring it — a % of PACKAGES. Never mixed with `pref`. */
  supply: Record<string, number>;
  pooled: number;
  /** §5's literal `pooled / pref`. Audit only — the two are on different scales. */
  ratio: number;
  /** The activity's share of stated demand, over the ten assessed rows. */
  prefShare?: number;
  /** Its share of what the destination sites actually sell, over the same rows. */
  supplyShare?: number;
  /** `supplyShare / prefShare`. 1.0 = parity. This is what `flag` bands. */
  index?: number;
  flag: GtmPackageGapFlag;
}

export interface GtmPackageAssessment {
  market: string;
  overall: number | null;
  ota: GtmPackageChannel;
  destination: GtmPackageChannel;
  sites: GtmPackageChannel[];
  siteWeights: number[];
  split: { ota: number; destination: number };
  rows: GtmPackageRow[];
  largestGap: { activity: string; short: string; pref: number; pooled: number; index?: number } | null;
  config: {
    gapThresholds: { undersupplied: number; oversupplied: number };
    /** Which quantity the thresholds band. "index" on any current server. */
    gapBasis?: string;
    heatAlpha: { base: number; range: number };
  };
  basis: {
    otaPlatforms: string[];
    substitutions: { specified: string; published: string; why: string }[];
    classifiedShare: Record<string, number>;
    source: string;
    /** Optional on the web side: the bundle ships independently of the API, so a
     *  UI ahead of the server must render without these rather than throw. */
    handPass?: { reviewedAt: string; scope: string; note: string } | null;
    /** Channel key → true where the published supply is the hand count. */
    handReadChannels?: Record<string, boolean>;
    /** Rows a catalogue holds for a site that this step does not assess, with the reason. */
    heldBack?: { site: string; label: string; shelf: string; rows: number; why: string }[];
    offSurvey?: { channel: string; label: string; packages: number; base: number }[];
  };
}

export interface GtmDistribution {
  iso: string;
  name: string;
  ready: boolean;
  requiredSources: GtmDistributionRequiredSource[];
  meta: GtmDistMeta | null;
  platformPresence: GtmDistPlatformPresence | null;
  listingCoverage: GtmDistListingCoverage | null;
  packageAssessment: GtmPackageAssessment | null;
  productMarketFit: GtmDistProductFit | null;
  pricing: GtmDistPricingMatrix | null;
  playbook: GtmDistPlaybookItem[] | null;
  experiences: GtmDistExperiences | null;
}

/** Only the markets with scraped distribution data (prototype-restricted dropdown). */
export function useGtmDistributionMarkets() {
  return useQuery({
    queryKey: ["dashboard", "gtm", "distribution", "markets"],
    queryFn: () => get<{ markets: { iso: string; name: string }[] }>("/api/v1/dashboard/gtm/distribution/markets"),
  });
}

export const gtmDistributionKey = (iso: string | null) => ["dashboard", "gtm", "distribution", iso] as const;

export function useGtmDistribution(iso: string | null) {
  return useQuery({
    queryKey: gtmDistributionKey(iso),
    queryFn: () => get<GtmDistribution>(`/api/v1/dashboard/gtm/distribution?iso=${iso}`),
    enabled: !!iso,
  });
}

export function useGtmDistributionExperiences(iso: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["dashboard", "gtm", "distribution", "experiences", iso],
    queryFn: () => get<{ items: GtmDistExperienceItem[] }>(`/api/v1/dashboard/gtm/distribution/experiences?iso=${iso}`),
    enabled: enabled && !!iso,
    staleTime: 60 * 60 * 1000,
  });
}

// GTM · Product (3.1 How to win). Market-independent product analysis of Saudi
// bookable leisure supply: L1 unique experiences × eight dimensions = L2
// products, benchmarked vs UAE / Egypt / Türkiye / Morocco, plus the
// demand-implied gap vs MT Demand Model targets. Mirrors
// server repos/gtm-product.ts (GtmProductPayload).
export interface GtmProductCountry {
  country: string;
  iso3: string;
}

export interface GtmProductCountView {
  totals: { label: string; iso3: string; count: number }[];
  byCategory: { category: string; saudi: number; bench: (number | null)[] }[];
  byRegion: { region: string; count: number }[];
}

export interface GtmProductCountryValue {
  country: string;
  iso3: string;
  value: number;
}

export interface GtmProductMixRow {
  country: string;
  iso3: string;
  values: number[];
}

export interface GtmProductMatrixRow {
  label: string;
  values: number[];
}

export interface GtmProductDistView {
  values: string[];
  mix: GtmProductMixRow[];
  byCategory: GtmProductMatrixRow[];
  byRegion: GtmProductMatrixRow[];
}

export interface GtmProductInclusionDim {
  name: string;
  unit: string;
  options: [string, string];
  overall: GtmProductCountryValue[];
  byCategory: { label: string; share: number }[];
  byRegion: { label: string; share: number }[];
}

export interface GtmProductGapDensityRow {
  country: string;
  iso3: string;
  count: number;
  arrivalsMillions: number | null;
  perMillion: number | null;
}

export interface GtmProductGapYear {
  year: number;
  targetMillions: number;
  required: number;
  gap: number;
}

export interface GtmProductGapRegion {
  destination: string;
  targetMillions: number;
  required: number;
  current: number;
  gap: number;
  observed: boolean;
}

export interface GtmProductGapBlock {
  unit: string;
  formula: string;
  current: number;
  currentLabel: string;
  density: { rows: GtmProductGapDensityRow[]; median: number };
  saudiPerMillion: number | null;
  years: GtmProductGapYear[];
  regions: GtmProductGapRegion[];
}

/** One benchmark country considered for a lifted share, kept or dropped. */
export interface GtmTargetMixBenchmark {
  country: string;
  iso3: string;
  value: number;
  kept: boolean;
  /** Why it was dropped; empty when kept. */
  reason: string;
}

/** One share the strategy wants MORE of, and the target the benchmarks imply. */
export interface GtmTargetMixLift {
  index: number;
  option: string;
  current: number;
  target: number;
  upliftPts: number;
  benchmarks: GtmTargetMixBenchmark[];
  /** True when no benchmark qualified, so the target held at today's value. */
  floored: boolean;
}

export interface GtmTargetMixDimension {
  name: string;
  direction: string;
  options: string[];
  current: number[];
  target: number[];
  lifts: GtmTargetMixLift[];
  holds: number[];
  absorbers: number[];
}

/** The websites behind Product Matching — site names, already in reading order,
 *  for the section's one-line `Source: …` credit. Null when the provenance tables
 *  aren't present. Mirrors the server's GtmProductSources. */
export interface GtmProductSources {
  marketplaces: string[];
  destinationMarketing: string[];
}

export interface GtmProduct {
  meta: {
    illustrative: boolean;
    source: string;
    process: string;
    targetSource: string;
    fetchedAt: string | null;
    benchmarks: GtmProductCountry[];
    arrivalsYear: number | null;
    sources: GtmProductSources | null;
  };
  framework: {
    sampleExperiences: { name: string; destination: string; category: string }[];
    dimensions: { name: string; options: { label: string; sub?: string }[] }[];
    example: { name: string; components: string; values: string[] } | null;
  };
  supply: GtmProductCountView;
  products: GtmProductCountView;
  composition: {
    mix: GtmProductMixRow[];
    bundleSize: GtmProductCountryValue[];
    top5Share: GtmProductCountryValue[];
    bundledShare: GtmProductCountryValue[];
  };
  duration: GtmProductDistView;
  geography: GtmProductDistView & { pairs: { pair: string; count: number }[] };
  inclusions: GtmProductInclusionDim[];
  timeslot: GtmProductDistView;
  quality: {
    stars: string[];
    mix: GtmProductMixRow[];
    byCategory: GtmProductMatrixRow[];
    byRegion: GtmProductMatrixRow[];
  };
  gap: {
    arrivals: { country: string; iso3: string; arrivalsMillions: number | null; year: number | null }[];
    product: GtmProductGapBlock | null;
    supply: GtmProductGapBlock | null;
    targetMix: { dimensions: GtmTargetMixDimension[] };
  };
}

export function useGtmProduct() {
  return useQuery({
    queryKey: ["dashboard", "gtm", "product"],
    queryFn: () => get<GtmProduct>("/api/v1/dashboard/gtm/product"),
  });
}

// Product Matching · 1.3 Product-segment matching. The demand side of the same
// product analysis: the client's seven inbound leisure segments (share of spend
// × top-10 activity preferences) scored against observed per-activity product
// counts. Mirrors `server/src/dashboard/repos/gtm-product-matching.ts`.
export interface GtmMatchingMarket {
  iso3: string;
  country: string;
  short: string;
  benchmark: boolean;
  products: number;
}

export interface GtmMatchingContribution {
  segmentId: string;
  segment: string;
  shareOfSpend: number;
  preference: number;
  contribution: number;
  rank: number;
}

export type GtmMatchingQuadrant = "build-first" | "well-served" | "watch" | "low-priority";
export type GtmMatchingStatus = "below" | "aligned" | "above";

export interface GtmMatchingActivity {
  activity: string;
  short: string;
  demandWeight: number;
  contributions: GtmMatchingContribution[];
  counts: number[];
  saudi: number;
  /** What the title classifier alone reported, kept for audit. Older APIs omit it. */
  saudiScript?: number;
  /** True where `saudi` is the hand-read count rather than the classifier's. */
  saudiHandRead?: boolean;
  benchmarkAvg: number;
  supplyGap: number;
  status: GtmMatchingStatus;
  quadrant: GtmMatchingQuadrant;
  /** Benchmark average too thin to read a gap from — shown, but held out of the thresholds. */
  thin: boolean;
}

export interface GtmMatchingSegmentRow {
  rank: number;
  activity: string;
  short: string;
  sourceLabel: string | null;
  preference: number;
  counts: number[];
  saudi: number;
  saudiScript?: number;
  saudiHandRead?: boolean;
  benchmarkAvg: number;
  status: GtmMatchingStatus;
  supplyGap: number;
  demandWeight: number;
  thin: boolean;
}

export interface GtmMatchingSegment {
  id: string;
  name: string;
  shareOfSpend: number;
  belowShare: number;
  rows: GtmMatchingSegmentRow[];
}

export interface GtmProductMatching {
  meta: {
    demandSource: string;
    supplySource: string;
    method: string;
    scrapedAt: string;
    unit: string;
    shareOfSpendTotal: number;
    thinCount: number;
    /**
     * Optional on the WEB side even though the server always sends it: the bundle
     * ships independently of the API, so a UI ahead of the server must render the
     * table without this rather than throw. Optional here makes the compiler
     * enforce that guard instead of leaving it to review.
     */
    manualPass?: { scope: string; reviewedAt: string; note: string };
    manualDiffCount?: number;
  };
  markets: GtmMatchingMarket[];
  segments: GtmMatchingSegment[];
  activities: GtmMatchingActivity[];
  thresholds: { demandWeight: number; supplyGap: number };
  worked: {
    activity: string;
    contributions: GtmMatchingContribution[];
    demandWeight: number;
    counts: number[];
    benchmarkAvg: number;
    supplyGap: number;
  } | null;
}

export function useGtmProductMatching() {
  return useQuery({
    queryKey: ["dashboard", "gtm", "product-matching"],
    queryFn: () => get<GtmProductMatching>("/api/v1/dashboard/gtm/product-matching"),
  });
}

// GTM · Product 1.2 tab 2 — key quality issues. The "why" behind the star mix:
// negative review text classified into a fixed issue taxonomy. Frequency is
// mentioning reviews over TOTAL negative reviews, so the values sum past 100 —
// one review can raise several issues. Quotes carry no reviewer identity.
export interface GtmQualityIssueProduct {
  product: string;
  mentions: number;
}

export interface GtmQualityIssueQuote {
  text: string;
  stars: number;
  product: string;
  platform: string;
}

export interface GtmQualityIssue {
  rank: number;
  issue: string;
  mentions: number;
  frequency: number;
  products: GtmQualityIssueProduct[];
  quotes: GtmQualityIssueQuote[];
}

export interface GtmReviewPlatform {
  platform: string;
  reviews: number;
}

export interface GtmReviewIssues {
  meta: {
    source: string;
    process: string;
    classifiedAt: string | null;
    awaitingClassification: boolean;
    reviewsRead: number;
    negativeReviews: number;
    untagged: number;
    /** The corpus by marketplace, biggest first — the panel's scale strip. */
    platforms: GtmReviewPlatform[];
    /** Reviews set aside as not written in English — NOT part of reviewsRead. */
    nonEnglish: number;
  };
  negativeAtOrBelow: number;
  issues: GtmQualityIssue[];
}

export function useGtmReviewIssues() {
  return useQuery({
    queryKey: ["dashboard", "gtm", "review-issues"],
    queryFn: () => get<GtmReviewIssues>("/api/v1/dashboard/gtm/review-issues"),
  });
}

// GTM · Visa (4.2). The per-passport decision tree — visa state (Passport
// Index) → priority gate → suppressed-demand check (gravity model) →
// competitor openness benchmark (UN VOI) → policy verdict. Every panel is
// nullable — the frontend renders only the checks the data can back.
/** A rule keyed on a document the traveller holds rather than on nationality. */
export interface SaudiEntryRule {
  key: string;
  /**
   * The market this rule belongs to. Null only for a rule the source states
   * irrespective of nationality — optional here for the same reason `rules` is:
   * a frontend can run ahead of a server that doesn't send the field yet.
   */
  iso3?: string | null;
  label: string;
  detail: string;
  conditions: string[];
  appliesWhen: string;
  /**
   * The rule is itself a route in (the substitute visa, the residence decree),
   * not a caveat on one. Optional for the same reason `rules` is; absent reads
   * as false.
   */
  grantsEntry?: boolean;
  source: string;
  sourceUrl: string | null;
  effectiveFrom: string | null;
}

export interface GtmVisaState {
  /** The published gradient: novisa | evisa | conditional | consular | refused. */
  cat: string;
  /** `novisa` or `evisa` only — a `conditional` market is NOT easy. */
  easy: boolean;
  visaFree: boolean;
  evisaEligible: boolean;
  voaEligible: boolean;
  /** Off the official list, but reachable by holding a qualifying document. */
  conditionalRoute?: boolean;
  /** On Saudi's official eVisa-eligible list. null when the overlay hasn't run. */
  officialEligible: boolean | null;
  /** Entry refused in general — not merely "apply at an embassy". */
  refused: boolean;
  /** The raw Passport Index category, so the UI can show what was corrected. */
  rawCat: string;
  requirementRaw: string;
  year: number;
  source: string;
  officialSource: string | null;
  /**
   * Document-based rules offered to this market, with their real conditions.
   * OPTIONAL on purpose: a frontend deployed ahead of the server — or pointed at
   * a backend that hasn't restarted — receives a payload without this field, and
   * a bare `.map()` on it white-screens the whole section. Read it through
   * `entryRules()` below, never directly.
   */
  rules?: SaudiEntryRule[];
}

/** Rules from a visa payload that may predate the field. Never returns undefined. */
export function entryRules(state: { rules?: SaudiEntryRule[] } | null | undefined): SaudiEntryRule[] {
  return state?.rules ?? [];
}

/**
 * The label every rule row prints under, in place of the source's own section
 * heading.
 *
 * `label` is the heading Wikipedia gives the section a rule was parsed out of —
 * "Substitute visa" — which names the source's filing system, not anything the
 * reader of this card is looking for. It stays in the record and stays in the
 * Sources block, where naming the section is exactly right for a citation.
 */
export const RULE_ROW_LABEL = "Exception";

/**
 * The label a granting route prints under on 4.2 — the same words section 2.3
 * uses for the same rule, for the same reason `RULE_ROW_LABEL` exists: the
 * source's own heading ("Substitute visa") names its filing system, not the
 * thing the reader is looking for.
 */
export const ROUTE_ROW_LABEL = "Conditional route";

export interface GtmVisaPriority {
  isPriority: boolean;
  bucket: string | null;
  haul: string | null;
  rank: number | null;
  prioritySetSize: number;
  universeSize: number;
  outboundM: number | null;
  momentumPct: number | null;
  momentumFrom: number;
  momentumTo: number;
}

export interface GtmVisaDemand {
  suppressed: boolean;
  actualSharePct: number | null;
  feasibleSharePct: number | null;
  currentVisitsK: number | null;
  feasibleVisitsK: number | null;
  gapK: number | null;
  opportunitySpendUsdM: number | null;
  opportunityYear: number;
  crossSectionYear: number;
}

export interface GtmVisaCompetitorRow {
  iso3: string;
  name: string;
  score: number | null;
  note: string | null;
  isSaudi: boolean;
}

export interface GtmVisaCompetitors {
  year: number | null;
  saudiScore: number | null;
  rows: GtmVisaCompetitorRow[];
  moreOpenCount: number;
  scoredCompetitors: number;
  outlier: boolean;
}

export type GtmVisaVerdictKey = "refused" | "noask" | "hold-priority" | "hold-demand" | "push-moderate" | "push-strong";

export interface GtmVisa {
  iso: string;
  name: string;
  state: GtmVisaState | null;
  priority: GtmVisaPriority | null;
  demand: GtmVisaDemand | null;
  competitors: GtmVisaCompetitors | null;
  verdict: GtmVisaVerdictKey | null;
}

export const gtmVisaKey = (iso: string | null) => ["dashboard", "gtm", "visa", iso] as const;

export function useGtmVisa(iso: string | null) {
  return useQuery({
    queryKey: gtmVisaKey(iso),
    queryFn: () => get<GtmVisa>(`/api/v1/dashboard/gtm/visa?iso=${iso}`),
    // The entry tier is normalized HERE, once, rather than in the section and
    // again in the printed brief — both render the same object, and a payload
    // from a server predating this gradient carries a `cat` that matches no
    // tier, which on 2.3's map silently dropped 68 countries. `entryCategoryOf`
    // passes a current payload straight through; only a stale one is upgraded.
    select: (d) =>
      d.state
        ? {
            ...d,
            state: {
              ...d.state,
              cat: entryCategoryOf(d.state, { conditionalRoute: grantsConditionalRoute(entryRules(d.state)) }),
            },
          }
        : d,
    enabled: !!iso,
  });
}

/** Download URL for the server-rendered PDF of a market's visa & entry playbook. */
export function gtmVisaPdfUrl(iso: string): string {
  return `/api/v1/dashboard/gtm/visa/pdf?iso=${encodeURIComponent(iso)}`;
}

// Saudi 1.2 — Source Market Prioritization (Plan 10 foundation). Real input
// panel per source market; the structural/feasible fair-share + opportunity
// fields are null until the regression lands (Plan 11), flagged by
// `meta.modelPending`.
export interface DriverContribution {
  driver: string;
  value: number;
}

export interface SourcePrioritizationMarket {
  iso: string;
  name: string;
  priority: boolean;
  bucket: string | null;
  haul: string | null;
  // inputs
  outboundM: number | null;
  actualSharePct: number | null;
  currentInboundK: number | null;
  religiousK: number | null;
  nonReligiousK: number | null;
  nonReligiousSharePct: number | null;
  spendPerVisitorUsd: number | null;
  momentumPct: number | null;
  spendMomentumPct?: number | null;
  spendPerVisitorMomentumPct?: number | null;
  distanceKm: number | null;
  mtTargetK?: number | null;
  mtTargetSharePct?: number | null;
  targetBaseVolumeK?: number | null;
  targetOpportunityK?: number | null;
  structuralBaseVolumeK?: number | null;
  structuralOpportunityK?: number | null;
  structuralOpportunitySpendUsdM?: number | null;
  feasibleBaseVolumeK?: number | null;
  feasibleOpportunityK?: number | null;
  feasibleOpportunitySpendUsdM?: number | null;
  // model — Estimator A (UI structural/feasible) + B (opportunity)
  structuralSharePctA: number | null;
  feasibleSharePctA: number | null;
  structuralSharePctB: number | null;
  feasibleSharePctB: number | null;
  feasibleSharePctBLow: number | null;
  feasibleSharePctBHigh: number | null;
  feasibleVolumeK: number | null;
  feasibleVolumeLowK: number | null;
  feasibleVolumeHighK: number | null;
  opportunityVolumeK: number | null;
  opportunityVolumeLowK: number | null;
  opportunityVolumeHighK: number | null;
  opportunitySpendUsdM: number | null;
  opportunitySpendLowUsdM: number | null;
  opportunitySpendHighUsdM: number | null;
  contribStructA: DriverContribution[] | null;
  contribFeasA: DriverContribution[] | null;
}

export interface ModelCoefficients {
  structLabels: string[];
  feasLabels: string[];
  aStruct: number[];
  aFeas: number[];
  aStructScale?: number;
  aFeasScale?: number;
  bPPML: number[];
  bStructAccess: number;
  bStructConnectivity: number | null;
  openCeil: number;
  connCeil: number | null;
  saudiOpen: number;
  connectivityEnabled: boolean;
}

export interface ModelSampleDrivers {
  iso: string;
  distanceKm: number | null;
  lnDistance: number;
  affinity: number;
  offering: number;
  openness: number;
  connectivity: number | null;
}

export interface SourcePrioritizationDiagnostics {
  trainingRows: number;
  estimatorA: { structR2: number; feasR2: number; inversions: number };
  estimatorB: {
    converged: boolean;
    iterations: number;
    deviance: number;
    openBeta: number;
    trace?: { iteration: number; coefficients: number[]; maxDelta: number | null }[];
  };
  coverage: number;
  coefficients: ModelCoefficients;
  sample: ModelSampleDrivers | null;
}

export type SourceDataStatus = "live" | "provided" | "extracted" | "computed" | "cached" | "mock" | "missing";

export interface LineageEntry {
  key: string;
  label: string;
  source: string;
  status: SourceDataStatus;
  modelPart?: string;
  table: string | null;
  detail: string;
}

export interface SourcePrioritizationResponse {
  meta: {
    crossSectionYear: number;
    opportunityYear: number;
    momentumFrom: number;
    momentumTo: number;
    hasEuromonitor: boolean;
    universeCount: number;
    indiaOutboundThousands: number | null;
    religiousSource: string | null;
    religiousYear: number | null;
    mtTargetYear?: number | null;
    mtTargetMarkets?: number;
    currentVisitorsSource?: "demand-model" | "euromonitor";
    currentVisitorsYear?: number | null;
    modelPending: boolean;
    diagnostics: SourcePrioritizationDiagnostics | null;
    lineage: LineageEntry[];
  };
  markets: SourcePrioritizationMarket[];
}

export const sourcePrioritizationQuery = (opportunityYear = 2025) => ({
  queryKey: ["dashboard", "source-prioritization", opportunityYear] as const,
  queryFn: () =>
    get<SourcePrioritizationResponse>(`/api/v1/dashboard/source-prioritization?opportunityYear=${opportunityYear}`),
});

export function useSourcePrioritization(opportunityYear = 2025) {
  return useQuery({
    ...sourcePrioritizationQuery(opportunityYear),
    // Keep the prior year's rows on screen while the new year refetches, so the
    // year toggle swaps values in place instead of flashing the empty/loading state.
    placeholderData: keepPreviousData,
  });
}

// --- Admin · Source Prioritization model ops ----------------------------

export interface AdminTrainingRow {
  rowIndex: number;
  sourceIso3: string;
  sourceName: string;
  destIso3: string;
  destName: string;
  tripsThousands: number;
  outboundThousands: number;
  share: number;
  lnShare: number | null;
  lnDistance: number;
  affinity: number;
  offering: number;
  openness: number;
  connectivity: number | null;
  olsEligible: boolean;
}

export interface AdminConnectivityRow {
  sourceIso3: string;
  destIso3: string;
  directFlights: number;
  oneStopFlights: number;
  twoStopFlights: number;
  weightedFlights: number;
  score: number;
  directPaths: number;
  oneStopPaths: number;
  twoStopPaths: number;
  hubCount: number;
  sourceMonth: string;
  refreshedAt: string;
}

export interface AdminTrainingRowsResponse {
  meta: {
    rowCount: number;
    returned: number;
    offset: number;
    limit: number;
    olsEligibleRows?: number;
    connectivityEnabled?: boolean;
  };
  rows: AdminTrainingRow[];
}

export interface AdminModelRun {
  id: string;
  opportunityYear: number;
  status: "queued" | "running" | "completed" | "failed" | string;
  stage: string;
  progress: number;
  isCurrent: boolean;
  startedAt: string | null;
  completedAt: string | null;
  promotedAt: string | null;
  createdAt: string;
  timings: Record<string, number>;
  diagnostics: SourcePrioritizationDiagnostics | null;
  error: string | null;
  sources?: LineageEntry[];
  counts?: {
    trainingRows: number;
    predictions: number;
    coefficients: number;
  };
}

export interface AdminCoefficient {
  estimator: string;
  label: string;
  value: number;
  iteration: number | null;
}

export interface AdminEvent {
  id: number;
  action: string;
  runId: string | null;
  actor: string;
  ip: string | null;
  userAgent: string | null;
  status: "requested" | "success" | "failed" | "blocked" | "rejected" | string;
  detail: Record<string, unknown>;
  createdAt: string;
}

export function useAdminSources(accessCode: string | null, opportunityYear = 2025) {
  return useQuery({
    queryKey: ["admin", "source-prioritization", "sources", opportunityYear],
    enabled: Boolean(accessCode),
    queryFn: () =>
      adminGet<{ sources: LineageEntry[] }>(
        `/api/v1/admin/source-prioritization/sources?opportunityYear=${opportunityYear}`,
        accessCode!,
      ),
  });
}

export function useAdminCurrentTrainingRows(accessCode: string | null, q: string, limit = 250) {
  return useQuery({
    queryKey: ["admin", "source-prioritization", "training-data", q, limit],
    enabled: Boolean(accessCode),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (q.trim()) params.set("q", q.trim());
      return adminGet<AdminTrainingRowsResponse>(
        `/api/v1/admin/source-prioritization/training-data?${params}`,
        accessCode!,
      );
    },
  });
}

export function useAdminConnectivityRows(accessCode: string | null) {
  return useQuery({
    queryKey: ["admin", "source-prioritization", "connectivity"],
    enabled: Boolean(accessCode),
    queryFn: () =>
      adminGet<{ rows: AdminConnectivityRow[] }>("/api/v1/admin/source-prioritization/connectivity", accessCode!),
  });
}

export function useAdminModelRuns(accessCode: string | null) {
  return useQuery({
    queryKey: ["admin", "source-prioritization", "runs"],
    enabled: Boolean(accessCode),
    refetchInterval: (query) => {
      const runs = (query.state.data as { runs: AdminModelRun[] } | undefined)?.runs ?? [];
      return runs.some((r) => r.status === "queued" || r.status === "running") ? 2000 : false;
    },
    queryFn: () => adminGet<{ runs: AdminModelRun[] }>("/api/v1/admin/source-prioritization/runs", accessCode!),
  });
}

export function useAdminEvents(accessCode: string | null, limit = 100) {
  return useQuery({
    queryKey: ["admin", "source-prioritization", "events", limit],
    enabled: Boolean(accessCode),
    queryFn: () =>
      adminGet<{ events: AdminEvent[] }>(`/api/v1/admin/source-prioritization/events?limit=${limit}`, accessCode!),
  });
}

export function useAdminModelRun(accessCode: string | null, runId: string | null) {
  return useQuery({
    queryKey: ["admin", "source-prioritization", "run", runId],
    enabled: Boolean(accessCode && runId),
    refetchInterval: (query) => {
      const run = (query.state.data as { run: AdminModelRun } | undefined)?.run;
      return run?.status === "queued" || run?.status === "running" ? 1500 : false;
    },
    queryFn: () => adminGet<{ run: AdminModelRun }>(`/api/v1/admin/source-prioritization/runs/${runId}`, accessCode!),
  });
}

export function useAdminRunCoefficients(accessCode: string | null, runId: string | null) {
  return useQuery({
    queryKey: ["admin", "source-prioritization", "coefficients", runId],
    enabled: Boolean(accessCode && runId),
    queryFn: () =>
      adminGet<{ coefficients: AdminCoefficient[] }>(
        `/api/v1/admin/source-prioritization/runs/${runId}/coefficients`,
        accessCode!,
      ),
  });
}

export function useStartAdminModelRun(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opportunityYear: number) =>
      adminPost<{ run: AdminModelRun }>("/api/v1/admin/source-prioritization/runs", accessCode!, { opportunityYear }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "source-prioritization"] });
    },
  });
}

export function usePromoteAdminModelRun(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      adminPost<{ run: AdminModelRun }>(`/api/v1/admin/source-prioritization/runs/${runId}/promote`, accessCode!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "source-prioritization"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "source-prioritization"] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin data management (upload docs → import → view → delete; refresh APIs)
// ─────────────────────────────────────────────────────────────────────────────

async function adminDelete<T>(path: string, accessCode: string): Promise<T> {
  const res = await fetch(path, { method: "DELETE", headers: { "x-admin-access-code": accessCode } });
  if (!res.ok) throw await adminError(path, res);
  return (await res.json()) as T;
}

async function adminUpload<T>(path: string, accessCode: string, form: FormData): Promise<T> {
  // No content-type header — the browser sets the multipart boundary.
  const res = await fetch(path, { method: "POST", headers: { "x-admin-access-code": accessCode }, body: form });
  if (!res.ok) throw await adminError(path, res);
  return (await res.json()) as T;
}

export interface AdminUploadSlot {
  sourceKind: string;
  label: string;
  formats: string[];
}

export interface AdminDownloadSlot {
  key: string;
  label: string;
  description: string;
  format: string;
}

export interface AdminImportFile {
  filePath: string;
  fileName: string;
  sourceKind: string;
  rowsImported: number;
  importedAt: string;
}

export interface AdminTableRowCount {
  label: string;
  rows: number;
}

export interface AdminDataSection {
  key: string;
  group: string;
  ref: string;
  title: string;
  description: string;
  mode: "upload" | "refresh" | "download";
  slots: AdminUploadSlot[];
  downloads: AdminDownloadSlot[];
  tables: AdminTableRowCount[];
  totalRows: number;
  imports: AdminImportFile[];
  lastImportedAt: string | null;
  refreshAvailable: boolean;
  refreshing: boolean;
  lastRefresh: AdminRefreshRun | null;
  schedule: AdminRefreshSchedule | null;
}

export interface AdminRefreshRun {
  trigger: "manual" | "cron" | "boot";
  status: "success" | "error";
  startedAt: string;
  finishedAt: string;
  durationMs: number | null;
  error: string | null;
}

export interface AdminRefreshSchedule {
  cronExpression: string;
  nextRunAt: string | null;
  lastCronRunAt: string | null;
}

export interface AdminDataPreview {
  columns: string[];
  rows: (string | number | boolean | null)[][];
  shown: number;
  total: number;
  offset: number;
  limit: number;
  rowKey: string[];
}

export const ADMIN_PREVIEW_PAGE = 50;

export function useAdminDataSections(accessCode: string | null) {
  return useQuery({
    queryKey: ["admin", "data", "sections"],
    enabled: Boolean(accessCode),
    // Poll while any section is mid-refresh so the UI reflects completion.
    refetchInterval: (query) => {
      const sections = (query.state.data as { sections: AdminDataSection[] } | undefined)?.sections ?? [];
      return sections.some((s) => s.refreshing) ? 3000 : false;
    },
    queryFn: () => adminGet<{ sections: AdminDataSection[] }>("/api/v1/admin/data/sections", accessCode!),
  });
}

export function useAdminSectionPreview(accessCode: string | null, key: string | null, tableIndex = 0) {
  return useInfiniteQuery({
    queryKey: ["admin", "data", "preview", key, tableIndex],
    enabled: Boolean(accessCode && key),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      adminGet<AdminDataPreview>(
        `/api/v1/admin/data/sections/${key}/preview?limit=${ADMIN_PREVIEW_PAGE}&offset=${pageParam}&table=${tableIndex}`,
        accessCode!,
      ),
    // Next offset is where this page ended; stop once we've loaded `total`.
    getNextPageParam: (last) => {
      const next = last.offset + last.shown;
      return last.shown > 0 && next < last.total ? next : undefined;
    },
  });
}

export function useUploadAdminDoc(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, sourceKind, file }: { key: string; sourceKind: string; file: File }) => {
      const form = new FormData();
      form.append("sourceKind", sourceKind);
      form.append("file", file);
      return adminUpload<{ result: { sourceKind: string; fileName: string; rowsImported: number } }>(
        `/api/v1/admin/data/sections/${key}/upload`,
        accessCode!,
        form,
      );
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "data"] });
      qc.invalidateQueries({ queryKey: ["admin", "data", "preview", vars.key] });
    },
  });
}

/**
 * Download a section's generated workbook.
 *
 * Fetched rather than linked: the route is behind the `x-admin-access-code`
 * header, which an <a download> cannot send. The response is saved through a
 * temporary object URL, and the server's Content-Disposition filename is used
 * when it sends one so the file is named the same way however it is fetched.
 */
export function useDownloadAdminFile(accessCode: string | null) {
  return useMutation({
    mutationFn: async ({ key, downloadKey }: { key: string; downloadKey: string }) => {
      const path = `/api/v1/admin/data/sections/${key}/download/${downloadKey}`;
      const res = await fetch(path, { headers: { "x-admin-access-code": accessCode ?? "" } });
      if (!res.ok) throw await adminError(path, res);
      const blob = await res.blob();
      const name =
        /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `${downloadKey}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { fileName: name, bytes: blob.size };
    },
  });
}

// Section 2.1 per-tile hero image: upload/replace. Invalidates the admin data
// queries AND the public behavioral-trends query so both the admin thumbnails
// and the dashboard wall pick up the new image without a manual reload.
export function useUploadTrendImage(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ trendId, file }: { trendId: string; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return adminUpload<{ result: { trendId: string; fileName: string; bytes: number } }>(
        `/api/v1/admin/data/behavioral-trends/${trendId}/image`,
        accessCode!,
        form,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "data"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "behavioral-trends"] });
    },
  });
}

export function useDeleteTrendImage(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trendId: string) =>
      adminDelete<{ result: { trendId: string; removed: boolean } }>(
        `/api/v1/admin/data/behavioral-trends/${trendId}/image`,
        accessCode!,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "data"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "behavioral-trends"] });
    },
  });
}

// Section 2.1 trend tile: soft-hide the whole row from the wall. The trend stays
// in the DB but drops out of every read; invalidates the public + admin queries
// so the row disappears from both the dashboard and the admin manager list.
export function useHideTrend(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trendId: string) =>
      adminDelete<{ result: { trendId: string; hidden: boolean } }>(
        `/api/v1/admin/data/behavioral-trends/${trendId}`,
        accessCode!,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "data"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "behavioral-trends"] });
    },
  });
}

export function useDeleteAdminSection(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      adminDelete<{ result: { tablesCleared: number; filesRemoved: number; filesFailed: number } }>(
        `/api/v1/admin/data/sections/${key}`,
        accessCode!,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "data"] }),
  });
}

export function useRefreshAdminSection(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      adminPost<{ result: { started: boolean } }>(`/api/v1/admin/data/sections/${key}/refresh`, accessCode!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "data", "sections"] }),
  });
}

export function useDeleteAdminRows(accessCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      rows,
      tableIndex = 0,
    }: {
      key: string;
      rows: Record<string, string | number>[];
      tableIndex?: number;
    }) =>
      adminPost<{ result: { deleted: number } }>(`/api/v1/admin/data/sections/${key}/delete-rows`, accessCode!, {
        rows,
        tableIndex,
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "data"] });
      qc.invalidateQueries({ queryKey: ["admin", "data", "preview", vars.key] });
    },
  });
}
